import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { bins, estimateTotal, messages } from '@fruitways/shared';
import { Button, EmptyState, ListRow, StatusLabel } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import { db } from '@/db/database';
import { useOnline } from '@/hooks/useOnline';
import { useOutbox } from '@/hooks/useOutbox';
import { useToast } from '@/hooks/useToast';
import { seasonForToday, useMasterData } from '@/hooks/useMasterData';

export function QueueScreen() {
  const master = useMasterData();
  const season = seasonForToday(master);
  const { online } = useOnline();
  const { drain } = useOutbox();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const items = useLiveQuery(async () => {
    const out = await db.outbox.orderBy('seq').toArray();
    const ests = await db.estimates.bulkGet(out.map((o) => o.estimateId));
    return out.map((o, i) => ({ item: o, estimate: ests[i] ?? null }));
  }, []);

  const farmName = (id: string) => master?.farms.find((f) => f.id === id)?.name ?? '';

  async function sendAll() {
    setBusy(true);
    const r = await drain();
    setBusy(false);
    if (r.remaining === 0) toast.show(messages.queue.sent);
  }

  return (
    <AppFrame
      title={messages.queue.title}
      subtitle={season ? messages.header.season(season.label) : undefined}
      action={<ProfileMenu />}
    >
      <div className="fw-content">
        {items && items.length === 0 && (
          <EmptyState icon="checkmark-done-outline" title={messages.queue.emptyTitle}>
            {messages.queue.emptyBody}
          </EmptyState>
        )}
        {items && items.length > 0 && (
          <div className="fw-rows">
            {items.map(({ item, estimate }) => (
              <ListRow
                key={item.id}
                title={
                  estimate
                    ? messages.queue.rowTitle(estimate.weekNumber, farmName(estimate.farmId))
                    : messages.detail.fallbackTitle
                }
                secondary={estimate?.lines.map((l) => l.varietyCode).join(', ')}
                meta={estimate ? bins(estimateTotal(estimate)) : undefined}
                status={<StatusLabel status="queued">{messages.status.queued}</StatusLabel>}
              />
            ))}
          </div>
        )}
      </div>
      {items && items.length > 0 && (
        <div style={{ flex: 'none', padding: '0 var(--screen-padding-x) var(--space-6)' }}>
          <Button icon="cloud-upload" onClick={sendAll} disabled={!online} loading={busy}>
            {messages.queue.sendAll}
          </Button>
        </div>
      )}
    </AppFrame>
  );
}
